/*
 * Elexa sticky add to cart bar (group A1): <elexa-sticky-atc>, rendered by sections/sticky-add-to-cart.liquid.
 *
 * - Shows when the main Add to cart button is more than 100px above the viewport (or after a scroll %),
 *   hidden while the cart drawer or a menu locks the page.
 * - "Add to cart" submits the page's <elexa-product> (same variant, quantity, selling plan and offer lines,
 *   same "select a variant" guard). Without a product section it adds the selected variant at quantity 1.
 * - Variant selects stay in sync with the main picker both ways (through <elexa-product>.setOption).
 * - Delivery date: today (store calendar) + N days, formatted in the storefront locale.
 */
(() => {
  if (customElements.get('elexa-sticky-atc')) return;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fill = (str, vars) => String(str || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));

  class ElexaStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      try {
        this.data = JSON.parse(this.querySelector('script[data-ex-sticky-json]').textContent);
      } catch (e) {
        this.data = { options: [], variants: [] };
      }
      this.button = this.querySelector('[data-ex-sticky-btn]');
      this.labelEl = this.querySelector('[data-ex-sticky-label]');
      this.errorEl = this.querySelector('[data-ex-sticky-error]');
      this.selects = Array.from(this.querySelectorAll('select[data-ex-sticky-option]'));
      this.behavior = this.dataset.behavior || 'add_to_cart';
      this.localOptions = (this.data.variants.find((v) => v.id === this.data.variantId) || {}).options || [];
      this.cleanups = [];
      this.renderDate();
      if (this.button) this.button.addEventListener('click', (e) => this.onClick(e));
      this.selects.forEach((s) => s.addEventListener('change', () => this.onSelect(s)));
      if (this.dataset.showNumbers === 'true') this.showSectionNumbers();
      this.startTimer = setTimeout(() => this.start(), 500);
    }

    disconnectedCallback() {
      clearTimeout(this.startTimer);
      clearTimeout(this.debounce);
      clearTimeout(this.hideTimer);
      clearInterval(this.safety);
      clearInterval(this.waitTimer);
      (this.cleanups || []).forEach((fn) => fn());
      document.querySelectorAll('[data-ex-section-number]').forEach((el) => el.remove());
      this.started = false;
    }

    controller() {
      const id = this.dataset.productId;
      return (
        document.querySelector(`elexa-product[data-ex-main-product][data-product-id="${id}"]`) ||
        document.querySelector(`elexa-product[data-product-id="${id}"]`)
      );
    }

    on(target, name, fn, opts) {
      target.addEventListener(name, fn, opts);
      this.cleanups.push(() => target.removeEventListener(name, fn, opts));
    }

    start() {
      const go = () => {
        const queue = () => this.schedule();
        this.on(window, 'scroll', queue, { passive: true });
        this.on(window, 'resize', queue, { passive: true });
        this.on(document, 'elexa:product-render', (e) => {
          const c = this.controller();
          if (c && e.detail && e.detail.sectionId === c.sectionId) this.sync(e.detail.state, c);
        });
        this.on(document, 'elexa:cart-updated', queue);
        this.on(document, 'elexa:product-error', (e) => {
          const c = this.controller();
          if (c && e.detail && e.detail.sectionId === c.sectionId && this.classList.contains('is-visible')) this.showError(e.detail.message);
        });
        this.on(document, 'click', (e) => {
          if (e.target.closest && e.target.closest('#cart-icon-bubble, a[href$="/cart"], [data-ex-cart-toggle]')) setTimeout(queue, 100);
        });
        const mo = new MutationObserver(queue);
        mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        const drawer = document.querySelector('cart-drawer');
        if (drawer) mo.observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] });
        this.cleanups.push(() => mo.disconnect());
        this.safety = setInterval(queue, 2000);
        const c = this.controller();
        if (c && c.state) this.sync(c.getState(), c);
        this.check();
      };
      if (this.dataset.trigger === 'scroll_percentage') {
        go();
        return;
      }
      let waited = 0;
      const ready = () => this.target();
      if (ready()) {
        go();
        return;
      }
      this.waitTimer = setInterval(() => {
        waited += 250;
        if (ready() || waited >= 10000) {
          clearInterval(this.waitTimer);
          if (ready()) go();
        }
      }, 250);
    }

    /** The main buy button to observe. */
    target() {
      const c = this.controller();
      if (c) {
        const btn = c.querySelector('[data-ex-atc]');
        if (btn) return btn;
      }
      return document.querySelector('[data-ex-main-product] [data-ex-atc], product-form [type="submit"]');
    }

    locked() {
      const b = document.body.classList;
      if (b.contains('overflow-hidden') || b.contains('overflow-hidden-mobile') || b.contains('overflow-hidden-tablet')) return true;
      const drawer = document.querySelector('cart-drawer');
      return Boolean(drawer && (drawer.classList.contains('active') || drawer.hasAttribute('open')));
    }

    shouldShow() {
      if (this.locked()) return false;
      if (this.dataset.trigger === 'scroll_percentage') {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) return false;
        return (window.scrollY / max) * 100 >= (parseFloat(this.dataset.triggerPercent) || 25);
      }
      const t = this.target();
      if (!t) return false;
      return t.getBoundingClientRect().bottom < -100;
    }

    schedule() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.check(), 50);
    }

    check() {
      const show = this.shouldShow();
      if (show === this.classList.contains('is-visible')) return;
      clearTimeout(this.hideTimer);
      if (show) {
        this.hidden = false;
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-visible')));
      } else {
        this.classList.remove('is-visible');
        this.hideTimer = setTimeout(() => {
          if (!this.classList.contains('is-visible')) this.hidden = true;
        }, reduced() ? 0 : 400);
      }
    }

    /* ---------------- state sync ---------------- */
    sync(state, c) {
      if (!state || this.behavior !== 'add_to_cart' || !this.button) return;
      const st = state.buttonState;
      const off = st === 'soldout' || st === 'unavailable';
      if (!this.busy) this.button.disabled = off;
      if (this.labelEl) {
        this.labelEl.textContent =
          st === 'soldout' ? this.dataset.soldOut : st === 'unavailable' ? this.dataset.unavailable : this.dataset.label;
      }
      this.selects.forEach((sel) => {
        const i = parseInt(sel.dataset.exStickyOption, 10) - 1;
        const value = state.options[i];
        Array.from(sel.options).forEach((o) => {
          const ok = c.variants.some(
            (v) => v.available && v.options[i] === o.value && state.options.every((x, k) => k === i || x == null || v.options[k] === x)
          );
          o.textContent = ok ? o.dataset.text : fill(this.dataset.unavailableValue, { value: o.dataset.text });
          o.disabled = !ok && o.value !== value;
        });
        if (value != null) sel.value = value;
      });
    }

    onSelect(sel) {
      const pos = parseInt(sel.dataset.exStickyOption, 10);
      const c = this.controller();
      if (c && typeof c.setOption === 'function') {
        c.setOption(pos, sel.value, { source: 'sticky' });
        return;
      }
      this.localOptions[pos - 1] = sel.value;
      const match = this.data.variants.find((v) => v.options.every((o, k) => o === this.localOptions[k]));
      this.localVariant = match || null;
      if (this.button) {
        const ok = Boolean(match && match.available);
        this.button.disabled = !ok;
        if (this.labelEl) this.labelEl.textContent = ok ? this.dataset.label : match ? this.dataset.soldOut : this.dataset.unavailable;
      }
    }

    /* ---------------- actions ---------------- */
    onClick(e) {
      const b = this.behavior;
      if (b === 'link') return;
      e.preventDefault();
      const smooth = reduced() ? 'auto' : 'smooth';
      if (b === 'scroll_to_section') {
        const n = parseInt(this.dataset.sectionNumber, 10);
        const el = n > 0 ? document.querySelectorAll('.shopify-section')[n - 1] : null;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - (parseInt(this.dataset.scrollOffset, 10) || 0);
        window.scrollTo({ top: Math.max(0, top), behavior: smooth });
        return;
      }
      if (b === 'scroll_to_percentage') {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        window.scrollTo({ top: Math.max(0, (max * (parseFloat(this.dataset.scrollPercent) || 50)) / 100), behavior: smooth });
        return;
      }
      this.addToCart();
    }

    async addToCart() {
      if (this.busy || !this.button || this.button.disabled) return;
      this.hideError();
      const c = this.controller();
      if (c && typeof c.submit === 'function') {
        c.submit(this.button);
        return;
      }
      const variant = this.localVariant || this.data.variants.find((v) => v.id === this.data.variantId);
      if (!variant || !variant.available || !window.Elexa || !Elexa.cart) return;
      this.busy = true;
      this.button.classList.add('loading');
      this.button.setAttribute('aria-disabled', 'true');
      const sp = this.button.querySelector('.loading__spinner');
      if (sp) sp.classList.remove('hidden');
      try {
        await Elexa.cart.add([{ id: variant.id, quantity: 1 }], { open: true, source: 'sticky-atc' });
      } catch (err) {
        this.showError((err && err.message) || '');
      } finally {
        this.busy = false;
        this.button.classList.remove('loading');
        this.button.removeAttribute('aria-disabled');
        if (sp) sp.classList.add('hidden');
      }
    }

    showError(message) {
      if (!this.errorEl || !message) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => this.hideError(), 6000);
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /* ---------------- delivery date ---------------- */
    renderDate() {
      const els = this.querySelectorAll('[data-ex-sticky-date]');
      if (!els.length) return;
      const days = parseInt(this.dataset.deliveryDays, 10) || 5;
      let y;
      let m;
      let d;
      if (window.Elexa && Elexa.time && typeof Elexa.time.storeParts === 'function') {
        const p = Elexa.time.storeParts();
        y = p.year;
        m = p.month - 1;
        d = p.day;
      } else {
        const now = new Date();
        y = now.getFullYear();
        m = now.getMonth();
        d = now.getDate();
      }
      const date = new Date(Date.UTC(y, m, d + days));
      const locale = (window.Elexa && Elexa.config && Elexa.config.locale) || document.documentElement.lang || undefined;
      const f = (opts) => {
        try {
          return new Intl.DateTimeFormat(locale, Object.assign({ timeZone: 'UTC' }, opts)).format(date);
        } catch (e) {
          return new Intl.DateTimeFormat(undefined, Object.assign({ timeZone: 'UTC' }, opts)).format(date);
        }
      };
      let text;
      switch (this.dataset.deliveryFormat) {
        case 'month_day_weekday':
          text = `${f({ month: 'long', day: 'numeric' })}, ${f({ weekday: 'long' })}`;
          break;
        case 'month_day':
          text = f({ month: 'long', day: 'numeric' });
          break;
        case 'day_month':
          text = `${f({ day: 'numeric' })} ${f({ month: 'long' })}`;
          break;
        default:
          text = f({ weekday: 'long', month: 'long', day: 'numeric' });
      }
      els.forEach((el) => {
        el.textContent = text;
      });
    }

    /* ---------------- editor helper ---------------- */
    showSectionNumbers() {
      const tpl = this.dataset.numberLabel || 'Section [n]';
      document.querySelectorAll('.shopify-section').forEach((section, i) => {
        if (section.querySelector(':scope > [data-ex-section-number]')) return;
        const badge = document.createElement('span');
        badge.className = 'ex-section-number';
        badge.setAttribute('data-ex-section-number', '');
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = fill(tpl, { n: i + 1 });
        if (getComputedStyle(section).position === 'static') section.style.position = 'relative';
        section.prepend(badge);
      });
    }
  }

  customElements.define('elexa-sticky-atc', ElexaStickyAtc);
})();
